import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import { DateFilterType, FilterDateSelect } from "components/FilterDateSelect";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import StyledTextField from "components/StyledTextField";
import { endOfDay } from "date-fns";
import { addressToPostalLocation } from "helpers";
import { useEffectOnce } from "helpers/useEffectOnce";
import { useDebounce } from "hooks/useDebounce";
import { isEqual } from "lodash";
import {
  PlannerFilter,
  PlannerFilterDateTypes,
  PlannerFilterStorageKey,
  defaultPlannerFilter,
} from "models/PlannerFilter";
import { TechnicianType } from "operations/schema/schema";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { getCustomers, resetPlannerFilter, setPlannerFilter } from "store/slices/planner.store";
import { isFlagEnabled } from "store/slices/user.store";

type PlannerFilterDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose: (dirty: boolean) => void;
};

export const PlannerFilterDialog: FC<PlannerFilterDialogProps> = (props) => {
  const { open, setOpen, onClose } = props;
  const intl = useIntl();
  const { areas, plannerNodes, symptoms, technicians } = useAppSelector((state) => state.cache);
  const { plannerFilter: filter, customers, loadingCustomers } = useAppSelector((s) => s.planner);
  const dispatch = useAppDispatch();
  const [jobId, setJobId] = useState<String>("");
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const nameFilter = useDebounce<string>(customerSearch, 500);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const areasFilterEnabled = useAppSelector((s) => isFlagEnabled(s, "AreasFilter"));

  const selectedArea = areas.find((a) => a.areaCode === filter.areaCode);
  const filteredTechnicians =
    (selectedArea?.technicians.map((x) =>
      technicians.find((e) => e.id === x),
    ) as TechnicianType[]) ?? [];
  const [hasDefault, setHasDefault] = useState<boolean>(false);

  useEffectOnce(() => {
    const plannerFilterString = localStorage.getItem(PlannerFilterStorageKey);
    if (plannerFilterString) {
      let storedPlannerFilter: PlannerFilter | null = null;
      storedPlannerFilter = JSON.parse(plannerFilterString) as PlannerFilter;
      if (storedPlannerFilter.specificDate)
        storedPlannerFilter.specificDate = new Date(storedPlannerFilter.specificDate);

      if (storedPlannerFilter != null) {
        dispatch(setPlannerFilter({ plannerFilter: storedPlannerFilter }));
        setHasDefault(true);
      }
    }
  });

  useEffect(() => {
    if (open) {
      dispatch(getCustomers({ nameFilter }));
    }
  }, [nameFilter, dispatch, open]);

  const techniciansList: TechnicianType[] = [
    {
      id: "Unassigned",
      name: intl.formatMessage({ id: "general.unassigned" }),
      guid: "Unassigned",
    },
    ...technicians,
  ];

  const dateOptions: DateFilterType[] = [
    {
      label: intl.formatMessage({ id: "filter.next7days" }),
      value: "week",
    },
    {
      label: intl.formatMessage({ id: "filter.nextMonth" }),
      value: "1month",
    },
    {
      label: intl.formatMessage({ id: "filter.next3months" }),
      value: "3month",
    },
    {
      label: intl.formatMessage({ id: "filter.specificDate" }),
      value: "specificDate",
    },
  ];

  const setFilter = useCallback(
    (filter: PlannerFilter) => {
      setIsDirty(true);
      dispatch(setPlannerFilter({ plannerFilter: filter }));
    },
    [dispatch],
  );

  const setDate = useCallback(
    (type: PlannerFilterDateTypes) => {
      var specificDate = null;
      if (type === "specificDate") specificDate = filter.specificDate;
      setFilter({
        ...filter,
        typeDate: type,
        jobId: null,
        specificDate: specificDate,
      });
    },
    [filter, setFilter],
  );
  const setSpecificDate = useCallback(
    (value: Date | null) => {
      setFilter({
        ...filter,
        specificDate: value ? endOfDay(value) : null,
        jobId: null,
        typeDate: "specificDate",
      });
    },
    [filter, setFilter],
  );

  const resetFilter = useCallback(() => {
    if (!isEqual(filter, defaultPlannerFilter)) {
      setIsDirty(true);
    }
    dispatch(resetPlannerFilter());
  }, [dispatch, filter]);

  const saveDefaultFilter = useCallback(() => {
    localStorage.setItem(PlannerFilterStorageKey, JSON.stringify(filter));
    setHasDefault(true);
  }, [filter]);

  const removeDefaultFilter = useCallback(() => {
    localStorage.removeItem(PlannerFilterStorageKey);
    setHasDefault(false);
  }, []);

  useEffect(() => {
    setJobId(filter.jobId || "");
  }, [filter, setJobId]);

  const handleClose = useCallback(() => {
    const dirty = isDirty;
    setOpen(false);
    setIsDirty(false);
    onClose(dirty);
  }, [isDirty, onClose, setOpen]);

  return (
    <FullScreenDialog
      title={intl.formatMessage({ id: "filter.setFilters" })}
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      child={
        <Container sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Autocomplete
                options={plannerNodes}
                getOptionLabel={(option) => `${option.label}`}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "filter.folder" })}
                  />
                )}
                value={filter.plannerNode}
                data-testid="PlannerFilterDialog-PlannerNodeFilter"
                onChange={(_, value) => {
                  setFilter({
                    ...filter,
                    plannerNode: value,
                    jobId: null,
                  });
                }}
                isOptionEqualToValue={(option, value) => option.nodeKey === value.nodeKey}
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                label={intl.formatMessage({ id: "filter.job" })}
                data-testid="PlannerFilterDialog-JobFilter"
                value={jobId}
                onChange={(e) => {
                  setFilter({
                    ...defaultPlannerFilter,
                    jobId: e.target.value,
                  });
                }}
                InputProps={{
                  endAdornment: filter.jobId ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilter({
                          ...filter,
                          jobId: null,
                        });
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : undefined,
                }}
              />
            </Grid>
            {areasFilterEnabled && (
              <Grid item xs={12}>
                <Autocomplete
                  options={areas}
                  getOptionLabel={(option) => `${option.areaCode}`}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label={intl.formatMessage({ id: "filter.areaCpde" })}
                    />
                  )}
                  value={selectedArea}
                  data-testid="PlannerFilterDialog-AreaCodeFilter"
                  onChange={(_, value) => {
                    setFilter({
                      ...filter,
                      areaCode: value?.areaCode || null,
                      selectedEngineers: [], // TODO we can filter to only keep the acceptable ones
                      jobId: null,
                    });
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={selectedArea ? filteredTechnicians : techniciansList}
                getOptionLabel={(option) => `${option.name}`}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "filter.engineers" })}
                  />
                )}
                value={filter.selectedEngineers}
                data-testid="PlannerFilterDialog-TechnicianFilter"
                onChange={(_, value) => {
                  setFilter({
                    ...filter,
                    selectedEngineers: value,
                    jobId: null,
                  });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={symptoms}
                getOptionLabel={(option) => `${option.description}`}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "filter.symptoms" })}
                  />
                )}
                value={filter.selectedSymptoms}
                data-testid="PlannerFilterDialog-SymptomFilter"
                onChange={(_, value) => {
                  setFilter({
                    ...filter,
                    selectedSymptoms: value,
                    jobId: null,
                  });
                }}
                isOptionEqualToValue={(option, value) => option.code === value.code}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                data-testid="PlannerFilterDialog-CustomerFilter"
                value={filter.customer}
                onChange={(_, value) => {
                  setFilter({
                    ...filter,
                    customer: value,
                    jobId: null,
                  });
                }}
                getOptionLabel={(o) => `${o.name}`}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.id}-${option.name}`} style={{ display: "block" }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.customerNo}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {addressToPostalLocation(option.address)}
                    </Typography>
                  </li>
                )}
                inputValue={customerSearch}
                onInputChange={(_, value) => {
                  setCustomerSearch(value);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x}
                options={customers}
                loading={loadingCustomers}
                renderInput={(params) => (
                  <StyledTextField
                    name="customer"
                    {...params}
                    label={intl.formatMessage({
                      id: "general.customer",
                    })}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Fragment>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label={intl.formatMessage({ id: "filter.city" })}
                data-testid="PlannerFilterDialog-CityFilter"
                value={filter.city || ""}
                onChange={(e) => {
                  setFilter({
                    ...filter,
                    city: e.target.value,
                    jobId: null,
                  });
                }}
                InputProps={{
                  endAdornment: filter.city ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilter({
                          ...filter,
                          city: null,
                          jobId: null,
                        });
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label={intl.formatMessage({ id: "general.postalCode" })}
                data-testid="PlannerFilterDialog-PostalCodeFilter"
                value={filter.postalCode || ""}
                onChange={(e) => {
                  setFilter({
                    ...filter,
                    postalCode: e.target.value,
                    jobId: null,
                  });
                }}
                InputProps={{
                  endAdornment: filter.postalCode ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilter({
                          ...filter,
                          postalCode: null,
                          jobId: null,
                        });
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FilterDateSelect value={filter.typeDate} options={dateOptions} onChange={setDate} />
            </Grid>
            {filter.typeDate === "specificDate" && (
              <Grid item xs={12}>
                <AsolviDatePicker
                  data-testid="PlannerFilterDialog-DatePickerFilter"
                  onChange={(value) => {
                    setSpecificDate(value);
                  }}
                  value={filter.specificDate}
                  label={intl.formatMessage({ id: "filter.specificDate" })}
                  clearable
                />
              </Grid>
            )}
            <Grid item xs={12} mt={2}>
              <SecondaryButton onClick={resetFilter} data-testid="PlannerFilterDialog-ResetButton">
                <FormattedMessage id="filter.resetAll" />
              </SecondaryButton>
            </Grid>
            {hasDefault ? (
              <Grid item xs={12}>
                <SecondaryButton
                  onClick={removeDefaultFilter}
                  sx={{ mt: 0 }}
                  data-testid="PlannerFilterDialog-RemoveAsDefaultButton"
                >
                  <FormattedMessage id="filter.removeAsDefault" />
                </SecondaryButton>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <SecondaryButton
                  onClick={saveDefaultFilter}
                  sx={{ mt: 0 }}
                  data-testid="PlannerFilterDialog-SetAsDefaultButton"
                >
                  <FormattedMessage id="filter.setAsDefault" />
                </SecondaryButton>
              </Grid>
            )}
            <Grid item xs={12}>
              <PrimaryButton
                data-testid="PlannerFilterDialog-SubmitButton"
                onClick={handleClose}
                sx={{ mt: 0 }}
              >
                <FormattedMessage id="filter.viewResults" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
